/**
 * Platform-aware OCR abstraction.
 *
 * Per the spec at /home/user/workspace/pogo_screen_ingest_spec.md §2, every
 * OCR module should route through this single interface so we can migrate
 * iOS to Apple Vision (free, on-device, fast) and Android to ML Kit
 * (free, on-device, offline) without touching the per-screen modules.
 *
 * Vision-only trial (trial/vision-only-no-colour): Tesseract has been retired
 * from the client entirely. iOS uses Apple Vision, Android uses ML Kit, and
 * there is no web/desktop OCR engine (that platform is decommissioned).
 *
 * The 9 existing OCR modules (catchOcr, detailOcr, inventoryOcr, medalOcr,
 * pokedexOcr, journalOcr, pokegenieOcr, tagsOcr) can be
 * migrated module by module by replacing their `worker.recognize(crop)`
 * calls with `recognizeRegions(source, [region])`. We do NOT touch them
 * in this PR; the modules keep working off the existing Tesseract path
 * while this abstraction is wired into the new video-ingest plumbing.
 *
 * Native plugin contracts:
 *   - iOS Apple Vision: @capacitor-community/image-to-text (or any
 *     equivalent VNRecognizeTextRequest bridge). Min iOS 13.
 *   - Android ML Kit: same @capacitor-community/image-to-text plugin,
 *     or a per-platform bridge. We stub the call so the freelancer can
 *     wire the Kotlin side without changing this file.
 */
import { isSharedFrameImage } from "./frameImageCache";
import { getPlatform } from "./platform";
import { getStatusBarFraction, resolveStatusBarExclusion } from "./statusBarInset";
// b13g G5e: queryClient is part of the eager app core (21 static importers).
// The two prior `await import("./queryClient")` call sites here were the only
// thing forcing it into a lazy chunk, blocking proper vendor splitting. This
// is a module-loading change only — no OCR/ROI logic is touched.
import { apiRequestRaw } from "./queryClient";
import { BUILD_ID } from "./buildId";
import { CANVAS_READBACK } from "./canvasReadback";

export interface OcrRegion {
  /** Normalized 0-1 coordinate of the top-left corner. */
  x: number;
  y: number;
  /** Normalized 0-1 width / height. */
  w: number;
  h: number;
}

export interface OcrResult {
  text: string;
  /** 0-1 confidence. We rescale Tesseract's 0-100 down to this range. */
  confidence: number;
  /**
   * Apple Vision ONLY: the recognised digits' union bounding box, as
   * normalised fractions (0-1, web/top-left origin) RELATIVE TO THE ROI that
   * was OCR'd. Tesseract and ML Kit do not provide per-glyph geometry, so this
   * is undefined for them; consumers MUST treat it as optional and fall back to
   * the pixel-centroid number-position heuristic when absent. The number-band
   * ROI spans the lower ~62% of the cell, so `box.y + box.h/2` (centroid within
   * the ROI) maps directly to the cell's center-vs-bottom number position.
   */
  box?: NumberBox;
  /**
   * Apple Vision ONLY: every recognised text observation with its own geometry,
   * in the order Vision returned them. Undefined on ML Kit and on older native
   * builds, so consumers MUST treat it as optional.
   */
  blocks?: OcrTextBlock[];
}

/** A normalised (0-1) rectangle, web/top-left origin. */
export interface NumberBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** One Vision text observation: its string plus ROI-relative web-coords box. */
export interface OcrTextBlock extends NumberBox {
  text: string;
  /** Vision native top-candidate confidence, 0-1. */
  conf: number;
}

export interface RecognizeOptions {
  /**
   * Tesseract-style character whitelist. Maps to
   * `tessedit_char_whitelist`. Ignored on Apple Vision / ML Kit, which
   * have no whitelist concept; callers fall back to the same regex
   * filters the per-screen modules already apply.
   */
  whitelist?: string;
  /**
   * Set when the caller has ALREADY cropped the input to a small, tight
   * region (e.g. a single pill slot). Pre-cropped reads must NOT be run
   * through the whole-frame guards: the status-bar clamp would shave the
   * top 6% off the tiny crop (clipping glyph tops / the star), and the
   * system-chrome scrub would delete legitimate labels that happen to look
   * like chrome - most importantly the Perfect variant "100%" pill label,
   * which /^[0-9]{1,3}\s*%$/ mistakes for a battery percentage. Those
   * guards exist for whole-frame OCR only; this flag opts a tight crop out
   * of both. Restores the pre-Apple-Vision Tesseract behaviour where
   * pill-slot text was passed through untouched.
   */
  precropped?: boolean;
  /**
   * Set when the caller OCRs a REGION WITH LAYOUT (a stat panel, a header) and
   * parses the result line by line, rather than a tight single-value crop.
   *
   * Apple Vision returns one observation per text run with no ordering promise
   * beyond its own internal grouping — on a PoGo profile it emits the whole
   * label column ("Pokémon Caught", "PokéStops Visited:", …) before the whole
   * value column ("170,155", "154,461", …). The native plugin concatenates
   * those runs into `text` with no separator, which is right for the digit-cell
   * ROIs it was built for and useless for a panel: labels end up welded to the
   * wrong numbers, or to each other.
   *
   * With this flag the observations are regrouped into visual rows from their
   * bounding boxes (see composeReadingOrderText), so a caller gets back
   * newline-separated rows in reading order with each label beside its own
   * value — the shape line-oriented parsers already expect.
   *
   * Opt-in by design. Whitelisted digit callers must NOT set it: their
   * whitelists strip the separators this adds, and some of them (e.g.
   * "0123456789 /") keep spaces, so a rebuilt row would change a read that
   * concatenation currently gets right.
   */
  multiline?: boolean;
}

// Vision-only trial (trial/vision-only-no-colour): Tesseract retired from the
// client. iOS routes to Apple Vision, Android to ML Kit; there is no longer a
// web/tesseract lane and no per-call engine forcing.
export type OcrEngineName = "apple_vision" | "ml_kit";

/**
 * Recognize text in one or more regions of an HTMLImageElement.
 * Region coordinates are normalized 0-1 to keep the call sites
 * resolution-agnostic; we crop to a canvas internally before handing the
 * pixel data to the platform engine.
 *
 * v1.2.1(10): every read is funnelled through `runWithOcrConcurrency` so
 * native Apple Vision / ML Kit calls cannot pile up (build 9 logged
 * dozens of concurrent `ImageToText detectText` calls), and the iOS
 * status-bar / Dynamic Island / Game Mode overlay band is clipped out
 * of the input region so OCR never reads system-chrome text like
 * "Game Mode: Off Access Game Overlay in Control Centre".
 */
export async function recognizeRegions(
  source: HTMLImageElement,
  regions: OcrRegion[],
  options?: RecognizeOptions,
): Promise<OcrResult[]> {
  // The same frame element asked the same question twice gets one native
  // pass (2026-09-22): the species-page probe and the My Pokémon probe both
  // OCR the WHOLE frame with `multiline` on every dwell frame, and since the
  // frame decoder is shared (lib/frameImageCache) they hand in the same
  // element. Keyed on the element, so a new frame never sees an old answer.
  const run = async () => {
    const engine = await pickEngine();
    const safe = regions.map(clampRegionBelowStatusBar);
    const out = await runOnEngineLane(engine, () => engine.recognizeRegions(source, safe, options));
    return out.map((res, i) =>
      remapGeometryToRequestedRegion(scrubSystemChromeText(res), regions[i], safe[i]),
    );
  };
  // Only a SHARED live frame is memoised; an element the caller decoded
  // itself keeps one native pass per call (per-crop fallback tests rely on it).
  if (!isSharedFrameImage(source)) return run();
  const key = JSON.stringify([regions, options ?? null]);
  let perImage = regionMemo.get(source);
  if (perImage) {
    const hit = perImage.get(key);
    if (hit) return hit;
  } else {
    perImage = new Map();
    regionMemo.set(source, perImage);
  }
  const memo = perImage;
  const pending = run();
  memo.set(key, pending);
  pending.catch(() => memo.delete(key));
  return pending;
}

const regionMemo = new WeakMap<HTMLImageElement, Map<string, Promise<OcrResult[]>>>();

/**
 * Express a result's `box` / `blocks` relative to the region the CALLER asked
 * for, when the region the engine actually read was the clamped one.
 *
 * The native side reports geometry relative to the ROI it was handed, and
 * `clampRegionBelowStatusBar` hands it a region that starts 6% lower than a
 * caller's whole-frame request. A caller mapping those boxes back with its
 * OWN region (myPokemonOcr's mapBlockToFrame) then places every block 0.06
 * too high and 6% too tall (2026-09-16: the My Pokémon extractor moved to a
 * whole-frame region and the IV bar strips, anchored to the Attack / Defense /
 * HP label boxes with a 0.002 tolerance, stopped landing on the bars, so no
 * live scan read IVs). A no-op whenever the clamp changed nothing, which is
 * every region that already starts at or below the status bar.
 */
export function remapGeometryToRequestedRegion(
  result: OcrResult,
  requested: OcrRegion,
  used: OcrRegion,
): OcrResult {
  if (
    used.x === requested.x &&
    used.y === requested.y &&
    used.w === requested.w &&
    used.h === requested.h
  ) {
    return result;
  }
  if (!result || (!result.box && !result.blocks)) return result;
  const rw = requested.w > 0 ? requested.w : 1;
  const rh = requested.h > 0 ? requested.h : 1;
  const map = <T extends NumberBox>(b: T): T => ({
    ...b,
    x: (used.x + b.x * used.w - requested.x) / rw,
    y: (used.y + b.y * used.h - requested.y) / rh,
    w: (b.w * used.w) / rw,
    h: (b.h * used.h) / rh,
  });
  return {
    ...result,
    box: result.box ? map(result.box) : result.box,
    blocks: result.blocks ? result.blocks.map(map) : result.blocks,
  };
}

/** Returns the engine name picked at runtime; mostly useful for diagnostics. */
export async function activeEngine(): Promise<OcrEngineName> {
  return (await pickEngine()).name;
}

/**
 * Per-platform minimum-confidence thresholds. Apple Vision (iOS) returns
 * a 0-1 confidence on a different distribution than Tesseract's 0-100;
 * a Vision read that says 0.55 is roughly as trustworthy as a Tesseract
 * read at 70/100. We expose two constants so callers can branch with a
 * single comparison instead of remembering platform quirks.
 *
 * Both constants are expressed on the SAME 0-100 scale that legacy
 * per-module code already uses. `runOcrFromDataUrl()` rescales for you.
 */
export const APPLE_VISION_MIN_CONFIDENCE = 55;
export const ML_KIT_MIN_CONFIDENCE = 55;

/**
 * Get the minimum-confidence floor appropriate for the currently active
 * engine. Use this when a caller wants a single comparison value:
 *
 *   if (result.conf < (await ocrMinConfidence())) { needsReview... }
 */
export async function ocrMinConfidence(): Promise<number> {
  const name = await activeEngine();
  if (name === "ml_kit") return ML_KIT_MIN_CONFIDENCE;
  // apple_vision (the only other engine) and any future default.
  return APPLE_VISION_MIN_CONFIDENCE;
}

/**
 * Convenience wrapper used by every per-screen OCR module.
 *
 * Takes a single pre-cropped data URL (the existing modules already crop
 * before calling OCR) and returns `{ text, conf }` on a 0-100 confidence
 * scale, matching the legacy per-module `runOcr()` signature so a
 * migration is a one-line drop-in.
 *
 * Internally:
 *  - On iOS native, calls Apple Vision via the ImageToText Capacitor
 *    plugin (when present at runtime).
 *  - On Android native, calls the same plugin (ML Kit on the device).
 *  - On web / when the plugin is missing, falls back to the existing
 *    Tesseract.js worker; passing `whitelist` keeps the legacy behaviour
 *    of restricting Tesseract output to a known character set.
 *
 * Confidence rescaling: Apple Vision and ML Kit return 0-1; we multiply
 * by 100 so callers can keep their 0-100 thresholds. Tesseract already
 * returns 0-100 so no change.
 */
export async function recognizeDataUrl(
  dataUrl: string,
  options?: RecognizeOptions,
): Promise<{ text: string; conf: number; box?: NumberBox; blocks?: OcrTextBlock[] }> {
  if (!dataUrl) return { text: "", conf: 0 };
  // Vision-only trial: every read uses the platform default engine (Apple
  // Vision on iOS, ML Kit on Android). Per-call engine forcing was removed
  // with Tesseract.
  const engine = await pickEngine();
  const img = await loadImageFromDataUrl(dataUrl);
  // Whole-image region: the caller already cropped, so we pass an identity
  // window through the engine. For whole-frame inputs we clamp below the
  // iOS status-bar band so system chrome stays out of OCR. For PRE-CROPPED
  // tight crops (e.g. a single pill slot) we skip the clamp: shaving 6%
  // off a tiny crop clips glyph tops / the star and is never wanted.
  const region = options?.precropped
    ? { x: 0, y: 0, w: 1, h: 1 }
    : clampRegionBelowStatusBar({ x: 0, y: 0, w: 1, h: 1 });
  const [raw] = await runOnEngineLane(engine, () =>
    engine.recognizeRegions(img, [region], options),
  );
  if (!raw) return { text: "", conf: 0 };
  // Pre-cropped reads bypass the system-chrome scrub: the scrub is meant
  // to drop whole-frame iOS overlay lines, but on a pill-slot crop it would
  // delete legitimate variant labels (the Perfect "100%" matches the
  // battery-% pattern). Pass slot text through untouched.
  const scrubbed = options?.precropped ? raw : scrubSystemChromeText(raw);
  // Layout callers rebuild rows from the per-observation boxes; the engine's
  // concatenated `text` has no usable line structure for them. Falls back to
  // `text` when the native build predates `blocks` (or on ML Kit), which is no
  // worse than today's behaviour.
  const text = options?.multiline
    ? composeReadingOrderText(scrubbed.blocks) || scrubbed.text
    : scrubbed.text;
  // `ocrEngine.recognizeRegions` normalises confidence to 0-1. Per-module
  // code wants 0-100, so multiply.
  return {
    text,
    conf: Math.round((scrubbed.confidence ?? 0) * 100),
    box: scrubbed.box,
    // Each observation with its box, for a caller that has to know WHERE a
    // piece of text sits and not only what it says.
    // Relative to the image passed in, not to the clamped region the engine
    // read. `box` above is left exactly as it was for its existing callers.
    blocks: scrubbed.blocks
      ? remapGeometryToRequestedRegion(
          { text: "", confidence: 0, blocks: scrubbed.blocks },
          { x: 0, y: 0, w: 1, h: 1 },
          region,
        ).blocks
      : undefined,
  };
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("recognizeDataUrl: image load failed"));
    img.src = dataUrl;
  });
}

interface InternalEngine {
  name: OcrEngineName;
  recognizeRegions(
    source: HTMLImageElement,
    regions: OcrRegion[],
    options?: RecognizeOptions,
  ): Promise<OcrResult[]>;
}

// ── Engine picker + runtime override ───────────────────────────────────────
//
// A/B without a rebuild: the engine is normally chosen by platform (iOS →
// apple_vision, Android → ml_kit), but a runtime override can
// force a specific engine. The override is persisted to localStorage under
// OCR_ENGINE_OVERRIDE_KEY so it survives reloads, and can be flipped live from
// the dev console:
//
//   localStorage.setItem("nexusdex.ocrEngine", "apple_vision"); location.reload();
//   // or, without a reload, from app code:
//   import { setOcrEngineOverride } from "@/lib/ocrEngine";
//   setOcrEngineOverride("apple_vision");
//
// Valid values: "apple_vision" | "ml_kit" | "auto" (clear). Default is "auto"
// — platform-based selection. apple_vision is already the platform default on
// iOS; the vision-only trial removed the Tesseract fallback entirely.

export const OCR_ENGINE_OVERRIDE_KEY = "nexusdex.ocrEngine";
export type OcrEngineOverride = OcrEngineName | "auto";

let _engine: InternalEngine | null = null;
let _overrideMemo: OcrEngineOverride | null = null;

function readStoredOverride(): OcrEngineOverride {
  if (_overrideMemo) return _overrideMemo;
  let stored: string | null = null;
  try {
    stored = typeof localStorage !== "undefined" ? localStorage.getItem(OCR_ENGINE_OVERRIDE_KEY) : null;
  } catch {
    stored = null;
  }
  _overrideMemo =
    stored === "apple_vision" || stored === "ml_kit" ? stored : "auto";
  return _overrideMemo;
}

/** Read the current OCR-engine override ("auto" when none is set). */
export function getOcrEngineOverride(): OcrEngineOverride {
  return readStoredOverride();
}

/**
 * Force a specific OCR engine at runtime (or pass "auto"/null to clear and
 * return to platform-based selection). Persists to localStorage and resets the
 * cached engine so the next `recognizeRegions` call picks up the change.
 */
export function setOcrEngineOverride(override: OcrEngineOverride | null): void {
  const value: OcrEngineOverride = override ?? "auto";
  _overrideMemo = value;
  try {
    if (typeof localStorage !== "undefined") {
      if (value === "auto") localStorage.removeItem(OCR_ENGINE_OVERRIDE_KEY);
      else localStorage.setItem(OCR_ENGINE_OVERRIDE_KEY, value);
    }
  } catch {
    /* localStorage unavailable (SSR / private mode) — in-memory memo still applies */
  }
  _engine = null;
}

async function loadEngineByName(name: OcrEngineName): Promise<InternalEngine> {
  if (name === "apple_vision") return loadAppleVisionEngine();
  return loadMlKitEngine();
}

async function pickEngine(): Promise<InternalEngine> {
  if (_engine) return _engine;
  const override = readStoredOverride();
  if (override !== "auto") {
    _engine = await loadEngineByName(override);
    return _engine;
  }
  const p = getPlatform();
  if (p === "ios") {
    _engine = await loadAppleVisionEngine();
  } else if (p === "android") {
    _engine = await loadMlKitEngine();
  } else {
    // Vision-only trial: the web/desktop platform is decommissioned and
    // Tesseract has been removed, so there is no engine for a non-mobile
    // platform. Fail loudly rather than silently falling back.
    throw new Error(
      `[ocrEngine] no OCR engine for platform "${p}": Tesseract was retired in the vision-only trial (iOS→apple_vision, Android→ml_kit only)`,
    );
  }
  return _engine;
}

/** Test-only: clear the cached engine + memoized override so the next pick re-reads state. */
export function __resetEngineForTests(): void {
  _engine = null;
  _overrideMemo = null;
}

// ── Apple Vision (iOS) ───────────────────────────────────────────────────

/** Per-ROI result row returned by the native DexVisionOcr batch plugin. */
export interface DexVisionRoiResult {
  id: string;
  text: string;
  /** Vision native top-candidate confidence, 0-1. */
  confidence: number;
  /**
   * Union digit bounding box, normalised (0-1, web/top-left origin) RELATIVE TO
   * THE ROI. Present only when Vision recognised at least one observation with a
   * box. Older native builds omit it; consumers fall back to the pixel-centroid.
   */
  box?: NumberBox;
  /**
   * Every text observation with its own geometry, unfiltered by `whitelist`.
   * Older native builds omit it, so `multiline` callers must tolerate its
   * absence (they fall back to the concatenated `text`).
   */
  blocks?: OcrTextBlock[];
}

/**
 * Per-call native timing the Android ML Kit plugin returns (2026-09-30): the
 * queue wait on its prep thread, the base64 decode + mosaic build, and the
 * ML Kit pass itself. iOS returns none of it (yet); every field is optional.
 */
export interface NativeOcrTiming {
  queueMs?: number;
  prepMs?: number;
  ocrMs?: number;
  mosaicW?: number;
  mosaicH?: number;
  timedOut?: boolean;
  stale?: boolean;
}

export interface DexVisionBatchResponse {
  results: DexVisionRoiResult[];
  /** Native deadline passed; every result is empty. */
  timedOut?: boolean;
  /** Skipped natively: it waited past the JS fence before it could start. */
  stale?: boolean;
  native?: NativeOcrTiming;
}

interface DexVisionOcrPlugin {
  recognizeDigits(args: {
    /** Base64 (raw or data: URL) of the FULL frame, handed off ONCE. */
    frame: string;
    /** Normalized 0-1 ROI rects, web/top-left origin. */
    rois: Array<{ id: string; x: number; y: number; w: number; h: number }>;
    /** Allowed characters; native side also post-filters to these. */
    whitelist?: string;
  }): Promise<DexVisionBatchResponse>;
}

/**
 * Rolling native OCR stats for the scan log (2026-09-30, Pixel 9a at 6 s a
 * frame). The admin "ms per frame" is one median per run and says nothing
 * about WHERE a frame's time went; this splits it: how many native calls a
 * session made, how long they queued natively, how long ML Kit itself took,
 * and how many hit the native deadline, were skipped as stale, or were
 * abandoned by the JS 5 s fence. Drained once per session by useDexLiveScan
 * into a "[dex-scan] native ocr" line.
 */
interface NativeOcrStats {
  calls: number;
  withTiming: number;
  queueMsSum: number;
  prepMsSum: number;
  ocrMsSum: number;
  ocrMsMax: number;
  nativeTimeouts: number;
  stale: number;
  jsTimeouts: number;
  lastMosaic: string | null;
}
const EMPTY_NATIVE_STATS: NativeOcrStats = {
  calls: 0,
  withTiming: 0,
  queueMsSum: 0,
  prepMsSum: 0,
  ocrMsSum: 0,
  ocrMsMax: 0,
  nativeTimeouts: 0,
  stale: 0,
  jsTimeouts: 0,
  lastMosaic: null,
};
let nativeOcrStats: NativeOcrStats = { ...EMPTY_NATIVE_STATS };

function noteNativeBatch(res: DexVisionBatchResponse | null | undefined): void {
  const st = nativeOcrStats;
  st.calls += 1;
  if (!res) return;
  if (res.timedOut) st.nativeTimeouts += 1;
  if (res.stale) st.stale += 1;
  const t = res.native;
  if (!t) return;
  st.withTiming += 1;
  st.queueMsSum += typeof t.queueMs === "number" ? t.queueMs : 0;
  st.prepMsSum += typeof t.prepMs === "number" ? t.prepMs : 0;
  const ocr = typeof t.ocrMs === "number" ? t.ocrMs : 0;
  st.ocrMsSum += ocr;
  st.ocrMsMax = Math.max(st.ocrMsMax, ocr);
  if (typeof t.mosaicW === "number" && t.mosaicW > 0) st.lastMosaic = `${t.mosaicW}x${t.mosaicH ?? 0}`;
}

/**
 * The session's native OCR summary, then reset. Null when no native batch
 * call ran (web, or a session that never reached OCR).
 */
export function drainNativeOcrStats(): {
  calls: number;
  queueMeanMs: number | null;
  prepMeanMs: number | null;
  ocrMeanMs: number | null;
  ocrMaxMs: number | null;
  nativeTimeouts: number;
  stale: number;
  jsTimeouts: number;
  lastMosaic: string | null;
} | null {
  const st = nativeOcrStats;
  nativeOcrStats = { ...EMPTY_NATIVE_STATS };
  if (st.calls === 0 && st.jsTimeouts === 0) return null;
  const mean = (sum: number) => (st.withTiming > 0 ? Math.round(sum / st.withTiming) : null);
  return {
    calls: st.calls,
    queueMeanMs: mean(st.queueMsSum),
    prepMeanMs: mean(st.prepMsSum),
    ocrMeanMs: mean(st.ocrMsSum),
    ocrMaxMs: st.withTiming > 0 ? st.ocrMsMax : null,
    nativeTimeouts: st.nativeTimeouts,
    stale: st.stale,
    jsTimeouts: st.jsTimeouts,
    lastMosaic: st.lastMosaic,
  };
}

/** Test seam. */
export function __resetNativeOcrStatsForTests(): void {
  nativeOcrStats = { ...EMPTY_NATIVE_STATS };
}

interface ImageToTextPlugin {
  /**
   * 2026-09-03: the Android plugin gained the same batch method as
   * DexVisionOcr (frame once, every ROI, ML Kit reads them in parallel
   * natively). Optional so an older native build keeps the per-crop path.
   */
  recognizeDigits?: DexVisionOcrPlugin["recognizeDigits"];
  detectText(args: { base64: string }): Promise<{
    text: string;
    confidence?: number;
    /**
     * Per-observation geometry, present on native builds that return it
     * (Android ML Kit since 2026-07-28; iOS Apple Vision via this legacy path
     * does not). Each box is normalised (0-1, top-left origin) RELATIVE TO THE
     * CROP the caller sent — i.e. ROI-relative, matching the batch DexVisionOcr
     * blocks. Consumers MUST treat it as optional. Without it, block-geometry
     * parsers (the species detail-page detector) get nothing and fall through.
     */
    blocks?: OcrTextBlock[];
  }>;
}

interface CapacitorBridgeShape {
  Plugins?: {
    /** feat/apple-vision-ocr: batch ROI digit plugin (preferred live path). */
    DexVisionOcr?: DexVisionOcrPlugin;
    /** Legacy whole-image Apple Vision / ML Kit plugin (per-crop). */
    ImageToText?: ImageToTextPlugin;
  };
}

function capacitorBridge(): CapacitorBridgeShape | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { Capacitor?: CapacitorBridgeShape }).Capacitor;
}

/**
 * Rescale Vision's native 0-1 confidence onto the distribution the pipeline
 * expects (Tesseract-on-0-1, i.e. `tesseractConfidence/100`). Apple Vision
 * clusters its confidence very differently from Tesseract; per the calibration
 * note above, a Vision read of ~0.55 is about as trustworthy as a Tesseract
 * read of 70/100. We anchor a monotonic piecewise-linear curve on that
 * equivalence point so `APPLE_VISION_MIN_CONFIDENCE` (55) and
 * `TESSERACT_MIN_CONFIDENCE` (50) live on a comparable scale once a caller
 * multiplies the 0-1 result by 100 in `recognizeDataUrl`.
 *
 *   0.00 -> 0.00,  0.55 -> 0.70,  1.00 -> 1.00 (monotonic between).
 */
export function rescaleVisionConfidence(v: number): number {
  if (!Number.isFinite(v)) return 0;
  const c = Math.min(1, Math.max(0, v));
  const ANCHOR_IN = 0.55;
  const ANCHOR_OUT = 0.7;
  if (c <= ANCHOR_IN) return (c / ANCHOR_IN) * ANCHOR_OUT;
  return ANCHOR_OUT + ((c - ANCHOR_IN) / (1 - ANCHOR_IN)) * (1 - ANCHOR_OUT);
}

/**
 * Drop every character not present in `whitelist`. This is the digit-constraint
 * for dex numbers: callers pass `"0123456789"` (or `"#0123456789"`), and any
 * stray glyph Vision emits is removed. The native plugin applies the same
 * filter; this is the defensive JS mirror so the constraint holds even if an
 * older native build returns unfiltered text.
 */
export function filterToWhitelist(text: string, whitelist?: string): string {
  if (!whitelist) return text;
  const allowed = new Set(whitelist.split(""));
  return text
    .split("")
    .filter((ch) => allowed.has(ch))
    .join("");
}

/**
 * Map the native batch plugin's per-ROI results back onto the ordered region
 * list the caller passed. Results are matched by id (the engine assigns
 * `roi_<index>` ids); a missing id yields an empty, zero-confidence result so
 * the output array always lines up 1:1 with the input regions. Each text is
 * whitelist-filtered and each confidence is Vision-rescaled.
 */
export function mapBatchRoiResults(
  regionIds: string[],
  pluginResults: DexVisionRoiResult[],
  whitelist?: string,
): OcrResult[] {
  const byId = new Map<string, DexVisionRoiResult>();
  for (const r of pluginResults) {
    if (r && typeof r.id === "string") byId.set(r.id, r);
  }
  return regionIds.map((id) => {
    const hit = byId.get(id);
    if (!hit) return { text: "", confidence: 0 };
    return {
      text: filterToWhitelist((hit.text || "").trim(), whitelist),
      confidence: rescaleVisionConfidence(
        typeof hit.confidence === "number" ? hit.confidence : 0,
      ),
      box: sanitizeNumberBox(hit.box),
      // Deliberately NOT whitelist-filtered: filtering strips the separators a
      // reading-order rebuild needs. `multiline` callers pass no whitelist.
      blocks: Array.isArray(hit.blocks) ? hit.blocks : undefined,
    };
  });
}

/**
 * Rebuild reading-order text from Vision's individual text observations.
 *
 * Vision hands back one observation per text run and groups them by its own
 * internal reading logic, which on a two-column layout means the entire left
 * column arrives before the entire right column. Concatenating those runs (what
 * the native plugin does for `text`) welds unrelated strings together:
 *
 *   "® Pokémon CaughtPokéStops Visited:XP Total XP:…170,155154,461…"
 *
 * Geometry is the only thing that recovers the layout. We group observations
 * into visual ROWS by vertical overlap, order the rows top-to-bottom and each
 * row's cells left-to-right, then join cells with a double space and rows with
 * a newline:
 *
 *   "® Pokémon Caught  170,155"
 *   "PokéStops Visited:  154,461"
 *
 * which is what label-anchored parsers are written against.
 *
 * Two observations share a row when they overlap vertically by more than half
 * the shorter one's height. That tolerates the baseline jitter between a label
 * and its value while still splitting genuinely stacked lines (e.g. a "BUDDY" /
 * "HISTORY" two-line button caption). Grouping is idempotent: if a Vision build
 * already merges a row into one observation, it simply stays one cell.
 *
 * Pure and exported for tests — the fixtures use observations captured from a
 * real device screenshot.
 */
export function composeReadingOrderText(blocks: OcrTextBlock[] | undefined): string {
  if (!blocks?.length) return "";
  const items = blocks
    .filter((b) => b && typeof b.text === "string" && b.text.trim().length > 0)
    .map((b) => ({
      text: b.text.trim(),
      x: Number.isFinite(b.x) ? b.x : 0,
      y: Number.isFinite(b.y) ? b.y : 0,
      h: Number.isFinite(b.h) && b.h > 0 ? b.h : 0.001,
    }))
    .sort((a, b) => a.y - b.y);
  if (items.length === 0) return "";

  const rows: Array<{ top: number; bottom: number; cells: typeof items }> = [];
  for (const it of items) {
    const row = rows[rows.length - 1];
    if (row) {
      const overlap = Math.min(it.y + it.h, row.bottom) - Math.max(it.y, row.top);
      if (overlap > 0.5 * Math.min(it.h, row.bottom - row.top)) {
        row.cells.push(it);
        // Widen the band so a tall cell keeps pulling in its shorter neighbours.
        row.top = Math.min(row.top, it.y);
        row.bottom = Math.max(row.bottom, it.y + it.h);
        continue;
      }
    }
    rows.push({ top: it.y, bottom: it.y + it.h, cells: [it] });
  }

  return rows
    .map((r) => r.cells.slice().sort((a, b) => a.x - b.x).map((c) => c.text).join("  "))
    .join("\n");
}

/**
 * Validate a raw plugin box into a usable NumberBox, or undefined. Guards
 * against NaN / out-of-range / zero-area boxes from a malformed native payload
 * so a bad box can never silently corrupt the number-position anchor (a missing
 * box cleanly falls back to the pixel-centroid). Pure; exported for tests.
 */
export function sanitizeNumberBox(box: NumberBox | undefined | null): NumberBox | undefined {
  if (!box) return undefined;
  const { x, y, w, h } = box;
  if (![x, y, w, h].every((v) => typeof v === "number" && Number.isFinite(v))) {
    return undefined;
  }
  if (w <= 0 || h <= 0) return undefined;
  if (x < 0 || y < 0 || x > 1 || y > 1) return undefined;
  return { x, y, w, h };
}

/**
 * Per-frame encode memo. An HTMLImageElement's pixels are immutable once it has
 * loaded, and the dex scanner builds a NEW element per captured frame, so the
 * element itself is a sound cache key — a WeakMap lets the entry die with the
 * frame.
 *
 * Why this exists: the dex cell path calls the engine once PER CELL. Without a
 * memo each of those calls re-encoded the same frame to JPEG from scratch, so a
 * 12-cell frame paid twelve full-frame encodes instead of one. The 2026-07-21
 * device log showed a 1066ms mean OCR frame against a 1000ms capture interval —
 * the pipeline could never catch up, and 238 frames were dropped as
 * `duplicate_in_flight` because the previous one was still running.
 */
const frameDataUrlCache = new WeakMap<HTMLImageElement, string>();

/** Encode the WHOLE source frame to a single data URL — handed off once. */
function fullFrameToDataUrl(img: HTMLImageElement): string {
  const memo = frameDataUrlCache.get(img);
  if (memo !== undefined) return memo;
  // The live frame arrives from native as a JPEG data URL and is loaded into
  // this element as-is, so the bytes to hand back are the ones it already
  // holds: no canvas draw, no re-encode (2026-09-03). ROIs are normalised, so
  // the frame's own resolution is exactly right.
  if (typeof img.src === "string" && img.src.startsWith("data:image/jpeg;base64,")) {
    return img.src;
  }
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, w);
  canvas.height = Math.max(1, h);
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  // No context (SSR / jsdom without the canvas package): NOT memoised, so a
  // later call in a real browser can still encode properly.
  if (!ctx) return "";
  ctx.drawImage(img, 0, 0);
  // JPEG keeps the single full-frame payload small (vs PNG per-crop fan-out);
  // large dex digits survive q=0.8 fine and Vision is robust to the artefacts.
  const url = canvas.toDataURL("image/jpeg", 0.8);
  frameDataUrlCache.set(img, url);
  return url;
}

/**
 * Build the batch Apple Vision engine backed by the custom DexVisionOcr plugin.
 * The whole frame is encoded ONCE per `recognizeRegions` call and all ROI rects
 * ride along in a single bridge call — this is the per-frame handoff that
 * avoids the base64-per-crop round trip. Falls back to Tesseract for the whole
 * batch if the native call throws.
 */
function buildDexVisionEngine(
  plugin: DexVisionOcrPlugin,
  name: OcrEngineName = "apple_vision",
  opts: { rethrowUnimplemented?: boolean } = {},
): InternalEngine {
  return {
    name,
    async recognizeRegions(source, regions, options) {
      if (regions.length === 0) return [];
      const ids = regions.map((_, i) => `roi_${i}`);
      const rois = regions.map((r, i) => ({
        id: ids[i],
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h,
      }));
      const frame = fullFrameToDataUrl(source);
      try {
        const res = await plugin.recognizeDigits({
          frame,
          rois,
          whitelist: options?.whitelist,
        });
        noteNativeBatch(res);
        const list = Array.isArray(res?.results) ? res.results : [];
        return mapBatchRoiResults(ids, list, options?.whitelist);
      } catch (err) {
        // A native build without the batch method says so once; the caller
        // (the Android loader) then switches to per-crop for the session.
        if (opts.rethrowUnimplemented && isUnimplementedError(err)) throw err;
        // Vision-only trial: Tesseract fallback removed. A failed native batch
        // degrades to empty, zero-confidence results (one per ROI) so a single
        // bad frame can't wedge the pipeline; the frame gate discards them.
        console.warn("[ocrEngine] DexVisionOcr batch failed, returning empty results", err);
        return ids.map(() => ({ text: "", confidence: 0 }));
      }
    },
  };
}

/** Capacitor's rejection for a plugin method the native side never registered. */
function isUnimplementedError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  if (e?.code === "UNIMPLEMENTED") return true;
  return /not implemented|unimplemented|method not found|no method/i.test(e?.message ?? "");
}

/**
 * Legacy whole-image Apple Vision / ML Kit engine backed by the ImageToText
 * plugin. Kept as a secondary fallback for builds that register ImageToText but
 * not the newer DexVisionOcr. One base64 per crop (the thing DexVisionOcr
 * exists to avoid), but better than dropping to Tesseract on iOS.
 */
function buildImageToTextEngine(
  plugin: ImageToTextPlugin,
  name: OcrEngineName,
): InternalEngine {
  return {
    name,
    async recognizeRegions(source, regions, options) {
      const out: OcrResult[] = [];
      for (const r of regions) {
        const dataUrl = cropToDataUrl(source, r);
        const base64 = dataUrl.split(",")[1] ?? dataUrl;
        try {
          const res = await plugin.detectText({ base64 });
          out.push({
            text: filterToWhitelist((res.text || "").trim(), options?.whitelist),
            confidence:
              typeof res.confidence === "number"
                ? rescaleVisionConfidence(res.confidence)
                : rescaleVisionConfidence(0.9),
            // Pass per-observation geometry straight through when the native
            // build provides it (Android ML Kit). Deliberately NOT whitelist-
            // filtered — blocks feed the reading-order rebuild + the detail-page
            // detector, both of which need the full label/word text, not digits.
            // Absent on native builds that don't return it, which is unchanged
            // (undefined) behaviour for every non-geometry caller.
            blocks: Array.isArray(res.blocks) ? res.blocks : undefined,
          });
        } catch (err) {
          // Vision-only trial: Tesseract fallback removed. Degrade this ROI to
          // an empty, zero-confidence result rather than dropping to Tesseract.
          console.warn("[ocrEngine] ImageToText call failed, returning empty result", err);
          out.push({ text: "", confidence: 0 });
        }
      }
      return out;
    },
  };
}

async function loadAppleVisionEngine(): Promise<InternalEngine> {
  // We never statically import a native plugin (it isn't present on web). At
  // runtime we prefer the batch DexVisionOcr plugin and fall back to the legacy
  // per-crop ImageToText plugin. Vision-only trial: the Tesseract fallback is
  // gone, so when neither native plugin is registered we throw rather than
  // silently degrade — there is no other engine on iOS.
  const plugins = capacitorBridge()?.Plugins;
  if (plugins?.DexVisionOcr) {
    return buildDexVisionEngine(plugins.DexVisionOcr);
  }
  if (plugins?.ImageToText) {
    console.warn("[ocrEngine] iOS: DexVisionOcr not found, using legacy ImageToText.");
    return buildImageToTextEngine(plugins.ImageToText, "apple_vision");
  }
  throw new Error(
    "[ocrEngine] iOS: no native OCR plugin found (DexVisionOcr / ImageToText). Tesseract was retired in the vision-only trial.",
  );
}

// ── Android ML Kit ───────────────────────────────────────────────────────

async function loadMlKitEngine(): Promise<InternalEngine> {
  const plugin = capacitorBridge()?.Plugins?.ImageToText;
  if (!plugin) {
    // Vision-only trial: Tesseract fallback removed. No ML Kit plugin means no
    // OCR engine on Android — fail loudly.
    throw new Error(
      "[ocrEngine] Android: ImageToText / ML Kit plugin not found. Tesseract was retired in the vision-only trial.",
    );
  }
  const perCrop = buildImageToTextEngine(plugin, "ml_kit");
  if (typeof plugin.recognizeDigits !== "function") return perCrop;
  // BATCH FIRST (2026-09-03). The per-crop engine cut every dex-number cell
  // to its own canvas, PNG-encoded it, sent it over the bridge and awaited
  // ML Kit one cell at a time: ~16 encodes + 16 native round trips per frame,
  // in series. This is the frame-once path iOS has had since the Apple
  // Vision port. A native build that predates recognizeDigits rejects the
  // first call as unimplemented, and the session drops to per-crop for good.
  const batch = buildDexVisionEngine(
    plugin as unknown as DexVisionOcrPlugin,
    "ml_kit",
    { rethrowUnimplemented: true },
  );
  let useBatch = true;
  return {
    name: "ml_kit",
    async recognizeRegions(source, regions, options) {
      if (useBatch) {
        try {
          return await batch.recognizeRegions(source, regions, options);
        } catch (err) {
          useBatch = false;
          console.warn("[ocrEngine] Android batch recognizeDigits unavailable, using per-crop", err);
        }
      }
      return perCrop.recognizeRegions(source, regions, options);
    },
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────

function cropToDataUrl(img: HTMLImageElement, r: OcrRegion): string {
  const x = Math.max(0, Math.floor(img.width * r.x));
  const y = Math.max(0, Math.floor(img.height * r.y));
  const w = Math.max(1, Math.floor(img.width * r.w));
  const h = Math.max(1, Math.floor(img.height * r.h));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return "";
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  return canvas.toDataURL("image/png");
}

// ── B10: server-side recognition fallback ───────────────────────────────────
//
// Escalation contract: per-screen OCR modules call recognizeDataUrl()
// for their first pass and inspect the returned confidence. When the
// caller is below the platform floor AND the trainer opted into server
// recognition for this scan, they can call escalateToServer() with the
// SAME crop bytes. The server tries Cloudflare Workers AI (LLaVA first,
// ResNet fallback) and returns its best read plus a confidence.
//
// Crops should typically be <50 KB. The server rejects anything over
// 256 KB to keep the round trip small.

export interface ServerEscalationOptions {
  /** 'species' | 'number' | 'text'. Tunes the server prompt. */
  hint?: "species" | "number" | "text";
  /** Optional nearby text to disambiguate (e.g. region name). */
  nearbyText?: string[];
}

export interface ServerEscalationResult {
  text: string;
  /** 0-100, server-supplied heuristic. */
  conf: number;
  candidates: string[];
  engine: "cf-vision" | "cf-workers-ai";
  cached: boolean;
}

/**
 * Send a single crop to /api/ocr/review for a second opinion. Returns
 * null on rate-limit, network failure, or 5xx so the caller can decide
 * whether to keep the low-confidence client result or surface a
 * "needs review" flag.
 */
export async function escalateToServer(
  dataUrl: string,
  options?: ServerEscalationOptions,
): Promise<ServerEscalationResult | null> {
  try {
    const base64 = dataUrl.startsWith("data:")
      ? dataUrl.slice(dataUrl.indexOf(",") + 1)
      : dataUrl;
    const res = await apiRequestRaw("POST", "/api/ocr/review", {
      imageBase64: base64,
      hint: options?.hint,
      context: options?.nearbyText ? { nearbyText: options.nearbyText } : undefined,
    });
    if (!res.ok) {
      if (res.status === 429) {
        console.info("[ocrEngine] server recognition rate-limited");
      }
      return null;
    }
    const body = (await res.json().catch(() => ({}))) as any;
    if (typeof body?.text !== "string") return null;
    return {
      text: body.text,
      conf: typeof body.confidence === "number" ? body.confidence : 0,
      candidates: Array.isArray(body.candidates) ? body.candidates : [],
      engine: body.engine === "cf-vision" ? "cf-vision" : "cf-workers-ai",
      cached: !!body.cached,
    };
  } catch (err) {
    console.warn("[ocrEngine] server escalation failed", err);
    return null;
  }
}

// ── v1.2.1(10): native OCR throttling + system-chrome filtering ─────────────
//
// Build 9 logs showed an unbounded number of `ImageToText detectText`
// calls running in parallel on iPhone (one per visible box per frame).
// Apple Vision is fast but not free — concurrent VNRecognizeTextRequests
// contend for the same neural-engine slot and push the WebView into
// frame-drop territory while also reading iOS overlay text like
// "Game Mode: Off" that bleeds into the captured frame.
//
// We add two cheap guards here so every code path that funnels through
// `recognizeRegions` / `recognizeDataUrl` benefits without any per-module
// changes:
//
//  1. `runWithOcrConcurrency` — a tiny FIFO semaphore. Allows at most
//     OCR_CONCURRENCY_LIMIT in-flight native calls; everything else
//     queues. This keeps the native side responsive and the JS thread
//     free of micro-task pile-ups.
//  2. `clampRegionBelowStatusBar` — drops the top statusBarExclusion()
//     of every region before it reaches the engine, which removes the
//     iOS clock / Dynamic Island / Game Mode overlay band from any
//     whole-frame OCR read.
//  3. `scrubSystemChromeText` — a regex pass that strips known iOS
//     overlay lines (Game Mode notice, Control Centre hint, time
//     stamps, battery %) from results.

// v1.2.1(13): bumped from 1 to 2 concurrent native calls, and every call
// is now wrapped in a 5s AbortController fence. Build-12 telemetry showed
// the serialised queue + an occasional hung Vision request was the actual
// cause of the user-reported "OCR freezes the app" — once one call hung,
// every subsequent cell waited forever. Bumping to 2 keeps Vision pressure
// bounded (still well below the 16-cell box-frame burst) and the timeout
// guarantees the queue drains even when Vision returns nothing.
const OCR_CONCURRENCY_LIMIT = 2;
const OCR_CALL_TIMEOUT_MS = 5000;
/**
 * Top band of the frame never read: the system status bar. 6% fits an
 * iPhone (Dynamic Island area ~59 pt of 852). Android (2026-09-23 Galaxy
 * S21+ log): the bar is ~84 px of a 2400 px screen (~3.5%) and GO's CP line
 * starts at ~5.5%, so the 6% clamp cut the tops of the CP digits and
 * "CP3083" read as 3083 / 308 / 83 / 3033 / nothing across frames, which is
 * what made the HUD flicker between "known" and "Appraise it to add IVs".
 */
const STATUS_BAR_EXCLUSION_IOS = 0.06;
const STATUS_BAR_EXCLUSION_ANDROID = 0.04;
/**
 * 2026-10-23: the platform number is a CEILING. On a native build the device's
 * own status-bar band (var(--nx-safe-top) over the screen height, see
 * lib/statusBarInset) lowers it when the bar is materially shorter: an iPad
 * reads from ~2.6%, an SE-class iPhone from ~3.6%. Notch and Dynamic Island
 * phones measure at or above the default and keep it, as does the Galaxy
 * S21+ on Android. The web keeps the iOS number: a browser's safe area says
 * nothing about the phone an uploaded screenshot came from.
 */
export function statusBarExclusion(): number {
  const platform = getPlatform();
  const fallback = platform === "android" ? STATUS_BAR_EXCLUSION_ANDROID : STATUS_BAR_EXCLUSION_IOS;
  if (platform === "web") return fallback;
  return resolveStatusBarExclusion(fallback, getStatusBarFraction());
}
const SYSTEM_CHROME_PATTERNS: RegExp[] = [
  /game\s*mode\s*[:\s]/i,
  /access\s+game\s+overlay/i,
  /control\s+cent(?:re|er)/i,
  /^[0-9]{1,2}:[0-9]{2}\s*(?:am|pm)?\s*$/i,
  /^[0-9]{1,3}\s*%$/,
  /^\s*recording\s*$/i,
  // v1.2.1(11): Nexus Dex's own UI tokens that leaked into OCR results
  // when the user briefly returned to our app mid-broadcast. Filtering
  // here belt-and-braces with the screen-classifier `nexus_ui` guard.
  /^\s*(?:nexus\s*dex|live\s*scan|profile|settings|events|dokémon|itemc|caught|seen|new)\s*$/i,
  /^\s*ad(?:venture|venture sync)\s*$/i,
  /^\s*the\s+red\s+status\s+bar\s+is/i,
];

let ocrActive = 0;
const ocrQueue: Array<() => void> = [];

function acquireOcrSlot(): Promise<void> {
  return new Promise((resolve) => {
    const tryAcquire = () => {
      if (ocrActive < OCR_CONCURRENCY_LIMIT) {
        ocrActive += 1;
        resolve();
      } else {
        ocrQueue.push(tryAcquire);
      }
    };
    tryAcquire();
  });
}

function releaseOcrSlot(): void {
  ocrActive = Math.max(0, ocrActive - 1);
  const next = ocrQueue.shift();
  if (next) next();
}

async function runWithOcrConcurrency<T>(fn: () => Promise<T>): Promise<T> {
  await acquireOcrSlot();
  try {
    // v1.2.1(13): hard deadline. If a single native call hangs (Vision
    // back-pressure on the iPhone, frame too big, GPU stall during
    // PoGo's own animations) the whole queue used to wedge because the
    // slot was never released. We race the call against a timeout that
    // throws — releaseOcrSlot() still fires in the finally block and
    // the dispatcher in LiveScanModal counts the throw as a frame drop.
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      return await Promise.race([
        fn(),
        new Promise<T>((_, reject) => {
          timer = setTimeout(() => {
            timer = null;
            nativeOcrStats.jsTimeouts += 1;
            reject(new Error("ocr_timeout"));
          }, OCR_CALL_TIMEOUT_MS);
        }),
      ]);
    } finally {
      // Cleared on settle (2026-09-30): the timer used to fire 5 s after
      // every answered call too, and the export counted 1564 "timeouts" on
      // 1569 calls that had all answered in ~150 ms.
      if (timer != null) clearTimeout(timer);
    }
  } finally {
    releaseOcrSlot();
  }
}

// Route OCR calls through the shared native lane. Vision-only trial: with
// Tesseract retired there is only one lane left — Apple Vision / ML Kit (the
// native single-batch path) keep the existing 2-slot semaphore + 5s deadline.
// The `engine` argument is retained for signature stability with the call
// sites; every remaining engine uses the same concurrency lane.
function runOnEngineLane<T>(_engine: InternalEngine, fn: () => Promise<T>): Promise<T> {
  return runWithOcrConcurrency(fn);
}

function clampRegionBelowStatusBar(r: OcrRegion): OcrRegion {
  const exclusion = statusBarExclusion();
  if (r.y >= exclusion) return r;
  const newY = exclusion;
  const trimmedH = Math.max(0.001, r.h - (newY - r.y));
  return { x: r.x, y: newY, w: r.w, h: trimmedH };
}

function isSystemChromeLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return true;
  return SYSTEM_CHROME_PATTERNS.some((pat) => pat.test(trimmed));
}

function scrubSystemChromeText(result: OcrResult): OcrResult {
  if (!result?.text) return result;
  const filtered = result.text
    .split(/\r?\n/)
    .filter((line) => !isSystemChromeLine(line))
    .join("\n");
  // Preserve the Vision digit box (geometry is independent of chrome-line
  // text filtering) so the number-position anchor survives scrubbing.
  return {
    text: filtered,
    confidence: result.confidence,
    box: result.box,
    // Scrub the blocks by the same rules. A `multiline` caller rebuilds its
    // text from these, so a chrome observation left here would walk straight
    // back into the result that the line filter above just removed it from.
    blocks: result.blocks?.filter((b) => !isSystemChromeLine(b?.text ?? "")),
  };
}

/**
 * Fire-and-forget timing sample to /api/perf/scan (which is also reachable
 * at /api/ocr/perf). Never throws, never blocks the caller. Aggregated by
 * the admin-only perf summary endpoint so we can compare engines
 * empirically without a UI build.
 */
export async function reportScanPerf(sample: {
  source: string;
  engine: string;
  imageSize?: number;
  ocrMs: number;
  totalMs: number;
  success?: boolean;
  /**
   * Why the run did not land (lib/scanOutcomes), only on a failure. Sent as
   * undefined (dropped from the JSON) when there is none, so a Worker before
   * migration 0100 sees the body it always saw.
   */
  outcome?: string;
  /**
   * Where a live run's timed frames went (scan_perf.ocr_frames / blur_drops,
   * 2026-10-26): how many reached the Pokédex grid OCR and how many the blur
   * gate dropped before it. Undefined on a screenshot batch (dropped from the
   * JSON), so a Worker before the columns sees the body it always saw.
   */
  ocrFrames?: number;
  blurDrops?: number;
}): Promise<void> {
  // The rejection is caught on the promise itself: a try/catch around a
  // `void`ed call catches nothing async, and the upload path's report ran
  // under jsdom (no origin for a relative URL) as an unhandled rejection
  // that failed CI (2026-09-16).
  try {
    // Which build ran it (scan_perf.build_id, migration 0108), so the admin
    // Devices tab can read one build's runs on their own. Dropped from the
    // JSON on a dev server ("unknown"), where the Worker then stores NULL.
    const body = { ...sample, buildId: BUILD_ID === "unknown" ? undefined : BUILD_ID };
    await apiRequestRaw("POST", "/api/perf/scan", body).catch(() => undefined);
  } catch {
    /* never blocks */
  }
}
